import { ArrowDownTrayIcon, ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid'
import { Badge, Button, Popover, PopoverContent, PopoverTrigger } from '@heroui/react'
import { Tooltip } from '@heroui/react'
import {
  FileStatus,
  findDownload,
  MediaType,
  useRequestDownload,
  // cancelFolderUpload,
} from '@qse/api'
import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore, useProgressTrackerStore } from '@qse/stores'
import { truncateTextMiddle, useIsTablet } from '@qse/utils'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { LogoIcon } from '..'
import { FileTypeIcon } from '../file-type'
import { useGlobalFileDownload } from '../hooks/useGlobalFileDownload'
import { getDownloadSummary } from '../hooks/useGlobalFileDownload'
import { useGlobalFileUpload } from '../hooks/useGlobalFileUpload'
import type { FolderFailuresModalData } from '../model-control/_components/folder-failures-modal'
import SharedProgressTracker, {
  AbortIcon,
  type BaseProgressItem,
  CheckIcon,
  ErrorIcon,
  SpinnerIcon,
  StatusIndicator,
  type TrackerConfig,
  UploadProgressBar,
} from './shared-progress-tracker'

// Convert DownloadItemState to match BaseProgressItem interface
interface ExtendedDownloadItemState extends BaseProgressItem {
  fileId?: string
  mediaType?: MediaType
  url?: string
  mimeType?: string
}

interface UploadFile extends BaseProgressItem {
  id: number
  originalName?: string
  serverName?: string // Server-assigned name (for naming conflicts)
  progress: number
  status: FileStatus
  mediaType: MediaType
  uploadStrategy?: 'single' | 'multipart' | 'folder'
  chunks?: number
  currentChunk?: number
  errorMessage?: string
  eta?: number
  uploadedFiles?: number
  totalFiles?: number
  encryptedFiles?: number
  failedUploads?: number
  failedEncryptions?: number
  uploadedBytes?: number
  totalBytes?: number
  startTime?: number
  mimeType?: string
  timeEstimate?: string
  isCancelling?: boolean // Track if file is being cancelled (waiting for serverFileId)
  failedFiles?: number
  failedFileDetails?: Array<{
    name: string
    path: string
    reason?: 'upload' | 'encryption' | 'deleted_destination' | 'zero_bytes'
  }>
  folderUploadId?: string
  parentPath?: string
  isRetrying?: boolean // [RETRY_FAILED] Indicates folder is retrying failed files
  // Encryption progress tracking (for individual files)
  encryptionProgress?: number // Encryption progress percentage (0-100)
  encryptionBytesProcessed?: number // Bytes processed during encryption
  encryptionTotalBytes?: number // Total bytes to encrypt
  encryptionThroughput?: number // Encryption throughput in bytes per second
  encryptionEta?: number // Estimated time remaining for encryption in seconds
}

/**
 * Cancelable Upload Icon Component
 * Shows a spinner by default, and an X icon on hover with a tooltip
 */
const CancelableUploadIcon: React.FC<{
  onCancel: () => void
}> = ({ onCancel }) => {
  const [isHovered, setIsHovered] = useState(false)

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onCancel()
  }

  return (
    <Tooltip content="Cancel Upload">
      <div
        className="relative w-6 h-6 cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onCancel()
          }
        }}
      >
        {isHovered ? (
          <XMarkIcon className="w-6 h-6 text-red-600 dark:text-[#DD524C] hover:text-red-700 dark:hover:text-red-500 transition-colors" />
        ) : (
          <SpinnerIcon />
        )}
      </div>
    </Tooltip>
  )
}

/**
 * Unified Progress Tracker that displays both upload and download trackers
 * with a shared mobile popover to avoid code duplication
 */
const ProgressTracker: React.FC = () => {
  const uploadStore = useGlobalFileUpload()
  const downloadStore = useGlobalFileDownload()
  const uiStore = useProgressTrackerStore()
  const requestDownload = useRequestDownload()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [isPopoverOpen, setIsPopoverOpen] = useState(true)
  const [activeTab, setActiveTab] = useState<'upload' | 'download'>('upload')
  const [isUploadExpanded, setIsUploadExpanded] = useState(true)
  const isTablet = useIsTablet()
  const pathname = useLocation().pathname

  // Handler for folder click - either show failures modal or navigate to folder
  const handleFolderClick = (file: UploadFile) => {
    const isFolder = file.mediaType === MediaType.FOLDER || file.uploadStrategy === 'folder'
    const isCompleted =
      file.status === FileStatus.AVAILABLE ||
      (file.status as string) === 'AVAILABLE' ||
      file.status === FileStatus.FAILED ||
      (file.status as string) === 'FAILED'

    if (!isFolder || !isCompleted) {
      return
    }

    const hasFailures = (file.failedFileDetails?.length || 0) > 0

    if (hasFailures) {
      // Show failures modal
      // [RETRY_FAILED] Compute whether any files are retryable (upload/zero_bytes with File ref)
      const retryableReasons = ['upload', 'zero_bytes']
      const hasRetryableFiles = (file.failedFileDetails || []).some(
        (d: any) => d.file && d.reason && retryableReasons.includes(d.reason),
      )

      const modalData: FolderFailuresModalData = {
        folderName: file.serverName || file.originalName || file.name,
        folderPath: file.parentPath,
        folderId: file.folderUploadId,
        failedFiles: file.failedFileDetails || [],
        uploadFailures: file.failedUploads || 0,
        encryptionFailures: file.failedEncryptions || 0,
        hasRetryableFiles, // [RETRY_FAILED]
      }

      useActiveModelStore.setState({
        data: modalData,
        width: 'max-w-2xl',
        modelActive: ModelControlEnum.FOLDER_FAILURES,
        toggleModel: true,
        scrollBehavior: 'inside',
        modalHeader: {
          title: `File Failures – ${file.serverName || file.originalName || file.name}`,
          showCloseButton: true,
        },
      })
    } else {
      // Navigate to folder
      if (file.folderUploadId) {
        navigate(`/folders/${file.folderUploadId}`)
      } else if (file.parentPath && file.parentPath !== '/') {
        navigate(file.parentPath)
      }
    }
  }

  // Sync visibility state with existing stores
  useEffect(() => {
    const shouldShowUpload = uploadStore.isUploadModalOpen && uploadStore.uploadingFiles.length > 0

    if (shouldShowUpload !== uiStore.uploadTracker.isVisible) {
      uiStore.setUploadVisible(shouldShowUpload)
    }
  }, [uploadStore.isUploadModalOpen, uploadStore.uploadingFiles.length, uiStore])

  useEffect(() => {
    const shouldShowDownload = downloadStore.isOpen && downloadStore.items.length > 0

    if (shouldShowDownload !== uiStore.downloadTracker.isVisible) {
      uiStore.setDownloadVisible(shouldShowDownload)
    }
  }, [downloadStore.isOpen, downloadStore.items.length, uiStore])

  // Auto-open popover when any progress is active
  useEffect(() => {
    const hasUploads = uploadStore.uploadingFiles.length > 0 && uploadStore.isUploadModalOpen
    const hasDownloads = downloadStore.items.length > 0 && downloadStore.isOpen

    if ((hasUploads || hasDownloads) && isTablet) {
      setIsPopoverOpen(true)
      // Set active tab based on what's available
      if (hasUploads && !hasDownloads) setActiveTab('upload')
      else if (hasDownloads && !hasUploads) setActiveTab('download')
      else if (hasUploads && hasDownloads) setActiveTab('upload') // Default to upload
    }
  }, [
    uploadStore.uploadingFiles.length,
    uploadStore.isUploadModalOpen,
    downloadStore.items.length,
    downloadStore.isOpen,
    isTablet,
  ])

  // Calculate positioning
  const uploadVisible = uiStore.uploadTracker.isVisible
  const downloadVisible = uiStore.downloadTracker.isVisible

  // Position calculation for side-by-side display
  const getPositionStyles = () => {
    // Use 500 width for trackers under xl breakpoint, 600 for xl and above t
    const trackerWidth = typeof window !== 'undefined' && window.innerWidth < 1280 ? 500 : 600

    if (uploadVisible && downloadVisible) {
      return {
        upload: { right: 8 },
        download: { right: 8 + trackerWidth + 12 },
      }
    }

    return {
      upload: { right: 8 },
      download: { right: 8 },
    }
  }

  const positions = getPositionStyles()

  // Helper functions for upload
  const getUploadTitle = () => {
    // Count folders and files separately
    const folderCount = uploadStore.uploadingFiles.filter(
      f => f.mediaType === MediaType.FOLDER,
    ).length
    const fileCount = uploadStore.uploadingFiles.filter(
      f => f.mediaType !== MediaType.FOLDER,
    ).length

    // Count by status for folders and files
    const folderErrors = uploadStore.uploadingFiles.filter(
      f =>
        f.mediaType === MediaType.FOLDER &&
        (f.status === FileStatus.FAILED || (f.status as string) === 'FAILED') &&
        !f.errorMessage?.includes('cancel') &&
        !f.errorMessage?.includes('abort'),
    ).length
    const fileErrors = uploadStore.uploadingFiles.filter(
      f =>
        f.mediaType !== MediaType.FOLDER &&
        (f.status === FileStatus.FAILED || (f.status as string) === 'FAILED') &&
        !f.errorMessage?.includes('cancel') &&
        !f.errorMessage?.includes('abort'),
    ).length
    const progressCount = uploadStore.uploadingFiles.filter(
      f => f.status === FileStatus.UPLOADING || (f.status as string) === 'UPLOADING',
    ).length
    const completedCount = uploadStore.uploadingFiles.filter(
      f => f.status === FileStatus.AVAILABLE || (f.status as string) === 'AVAILABLE',
    ).length
    const encryptingCount = uploadStore.uploadingFiles.filter(
      f => f.status === FileStatus.ENCRYPTING || (f.status as string) === 'ENCRYPTING',
    ).length

    // Debug: Log failed files to see what's happening
    const failedFiles = uploadStore.uploadingFiles.filter(
      f => f.status === FileStatus.FAILED || (f.status as string) === 'FAILED',
    )

    if (failedFiles.length > 0) {
      console.log(
        '🔍 Failed files in progress tracker:',
        failedFiles.map(f => ({
          name: f.name,
          status: f.status,
          errorMessage: f.errorMessage,
          mediaType: f.mediaType,
        })),
      )
    }
    const folderAborted = uploadStore.uploadingFiles.filter(
      f =>
        f.mediaType === MediaType.FOLDER &&
        (f.status === FileStatus.FAILED || (f.status as string) === 'FAILED') &&
        (f.errorMessage?.includes('cancel') || f.errorMessage?.includes('abort')),
    ).length
    const fileAborted = uploadStore.uploadingFiles.filter(
      f =>
        f.mediaType !== MediaType.FOLDER &&
        (f.status === FileStatus.FAILED || (f.status as string) === 'FAILED') &&
        (f.errorMessage?.includes('cancel') || f.errorMessage?.includes('abort')),
    ).length
    const folderProgress = uploadStore.uploadingFiles.filter(
      f =>
        f.mediaType === MediaType.FOLDER &&
        (f.status === FileStatus.UPLOADING || (f.status as string) === 'UPLOADING'),
    ).length
    const fileProgress = uploadStore.uploadingFiles.filter(
      f =>
        f.mediaType !== MediaType.FOLDER &&
        (f.status === FileStatus.UPLOADING || (f.status as string) === 'UPLOADING'),
    ).length
    const folderCompleted = uploadStore.uploadingFiles.filter(
      f =>
        f.mediaType === MediaType.FOLDER &&
        (f.status === FileStatus.AVAILABLE || (f.status as string) === 'AVAILABLE'),
    ).length
    const fileCompleted = uploadStore.uploadingFiles.filter(
      f =>
        f.mediaType !== MediaType.FOLDER &&
        (f.status === FileStatus.AVAILABLE || (f.status as string) === 'AVAILABLE'),
    ).length
    const folderEncrypting = uploadStore.uploadingFiles.filter(
      f =>
        f.mediaType === MediaType.FOLDER &&
        (f.status === FileStatus.ENCRYPTING || (f.status as string) === 'ENCRYPTING'),
    ).length
    const fileEncrypting = uploadStore.uploadingFiles.filter(
      f =>
        f.mediaType !== MediaType.FOLDER &&
        (f.status === FileStatus.ENCRYPTING || (f.status as string) === 'ENCRYPTING'),
    ).length

    // Calculate totals
    const errorCount = folderErrors + fileErrors
    const abortedUploadsCount = folderAborted + fileAborted

    // Debug: Log error counts
    console.log('🔍 Progress tracker error counts:', {
      folderErrors,
      fileErrors,
      totalErrors: errorCount,
      totalFiles: uploadStore.uploadingFiles.length,
    })

    // Helper function to create mixed content descriptions
    const createMixedDescription = (folderCount: number, fileCount: number, action: string) => {
      if (folderCount > 0 && fileCount > 0) {
        return `${folderCount} folder${folderCount > 1 ? 's' : ''} and ${fileCount} file${fileCount > 1 ? 's' : ''} ${action}`
      } else if (folderCount > 0) {
        return `${folderCount} folder${folderCount > 1 ? 's' : ''} ${action}`
      } else {
        return `${fileCount} file${fileCount > 1 ? 's' : ''} ${action}`
      }
    }

    // Remove folder-only simplistic override so we always reflect actual statuses

    // Prioritize active uploads over canceled ones
    // If there are active uploads (uploading/encrypting), show those first
    if (progressCount > 0) {
      return createMixedDescription(folderProgress, fileProgress, 'uploading')
    }
    if (encryptingCount > 0) {
      return createMixedDescription(folderEncrypting, fileEncrypting, 'encrypting')
    }

    // Handle mixed error, completed, and aborted states (only when no active uploads)
    if (errorCount > 0 && completedCount > 0 && abortedUploadsCount > 0) {
      const completedDesc = createMixedDescription(folderCompleted, fileCompleted, 'completed')
      const abortedDesc = createMixedDescription(folderAborted, fileAborted, 'aborted')
      const errorDesc = createMixedDescription(folderErrors, fileErrors, 'failed')

      return `${completedDesc}, ${abortedDesc}, ${errorDesc}`
    }
    if (errorCount > 0 && completedCount > 0) {
      const completedDesc = createMixedDescription(folderCompleted, fileCompleted, 'completed')
      const errorDesc = createMixedDescription(folderErrors, fileErrors, 'failed')

      return `${completedDesc}, ${errorDesc}`
    }
    if (abortedUploadsCount > 0 && completedCount > 0) {
      const completedDesc = createMixedDescription(folderCompleted, fileCompleted, 'completed')
      const abortedDesc = createMixedDescription(folderAborted, fileAborted, 'aborted')

      return `${completedDesc}, ${abortedDesc}`
    }
    if (abortedUploadsCount > 0 && errorCount > 0) {
      const abortedDesc = createMixedDescription(folderAborted, fileAborted, 'aborted')
      const errorDesc = createMixedDescription(folderErrors, fileErrors, 'failed')

      return `${abortedDesc}, ${errorDesc}`
    }
    if (errorCount > 0) {
      return createMixedDescription(folderErrors, fileErrors, 'failed')
    }
    if (abortedUploadsCount > 0) {
      return createMixedDescription(folderAborted, fileAborted, 'aborted')
    }
    if (completedCount > 0) {
      return createMixedDescription(folderCompleted, fileCompleted, 'completed')
    }
    if (errorCount > 0) return `${errorCount} upload failed`
    if (abortedUploadsCount > 0)
      return `${abortedUploadsCount} upload${abortedUploadsCount > 1 ? 's' : ''} aborted`
    if (progressCount > 0) return `${progressCount} file${progressCount > 1 ? 's' : ''} uploading`
    if (encryptingCount > 0)
      return `${encryptingCount} file${encryptingCount > 1 ? 's' : ''} encrypting`
    if (completedCount > 0)
      return `${completedCount} upload${completedCount > 1 ? 's' : ''} completed`

    // Default case - show mixed content description
    return createMixedDescription(folderCount, fileCount, 'upload progress')
  }

  const getUploadTitleColor = () => {
    const errorCount = uploadStore.uploadingFiles.filter(
      f => f.status === FileStatus.FAILED || (f.status as string) === 'FAILED',
    ).length
    const progressCount = uploadStore.uploadingFiles.filter(
      f => f.status === FileStatus.UPLOADING || (f.status as string) === 'UPLOADING',
    ).length
    const encryptingCount = uploadStore.uploadingFiles.filter(
      f => f.status === FileStatus.ENCRYPTING || (f.status as string) === 'ENCRYPTING',
    ).length

    if (errorCount > 0) return 'text-red-600 dark:text-[#DD524C]'
    if (progressCount > 0) return 'text-gray-900 dark:text-white'
    if (encryptingCount > 0) return 'text-blue-600 dark:text-blue-400'

    return 'text-gray-900 dark:text-white'
  }

  const getUploadProgressCount = () => {
    // Count only UPLOADING files (not ENCRYPTING) for cancel functionality
    const activeUploads = uploadStore.uploadingFiles.filter(
      f => f.status === FileStatus.UPLOADING || (f.status as string) === 'UPLOADING',
    )

    return activeUploads.length
  }

  // Helper functions for download
  const getDownloadTitleColor = () => {
    const errorCount = downloadStore.items.filter(i => i.status === 'FAILED').length
    const inProgressCount = downloadStore.items.filter(
      i => i.status === 'PROCESSING' || i.status === 'DECRYPTING' || i.status === 'PREPARING',
    ).length

    if (errorCount > 0) return 'text-red-600 dark:text-[#DD524C]'
    if (inProgressCount > 0) return 'text-gray-900 dark:text-white'

    return 'text-gray-900 dark:text-white'
  }

  // Upload handlers
  const handleUploadClose = () => {
    uploadStore.setIsUploadModalOpen(false)
    uploadStore.clearCompleted()
    uiStore.hideUploadTracker()
  }

  const handleUploadRetry = (id: string | number) => {
    const fileId = typeof id === 'string' ? parseInt(id) : id

    uploadStore.handleRetry(fileId)
  }

  // Download handlers
  const handleDownloadClose = () => {
    downloadStore.setOpen(false)
    downloadStore.clearCompleted()
  }

  const handleDownloadRetry = (id: string | number) => {
    const stringId = id.toString()

    downloadStore.retry(stringId)

    const item = downloadStore.items.find(i => i.id === stringId)

    if (item?.fileId) {
      requestDownload
        .mutateAsync({ id: item.fileId })
        .then(res => {
          if (res.status === 'AVAILABLE' && res.url) {
            downloadStore.markAvailable(stringId, res.url)
          } else {
            downloadStore.markProcessing(stringId)

            const interval = window.setInterval(async () => {
              try {
                const info = await findDownload({ id: item.fileId })

                if (info.status === 'AVAILABLE' && info.url) {
                  window.clearInterval(interval)
                  downloadStore.markAvailable(stringId, info.url)
                }
              } catch {
                // ignore polling errors
              }
            }, 2000)
          }
        })
        .catch(err => {
          downloadStore.markFailed(stringId, err?.message || 'Failed')
        })
    }
  }

  // Upload configuration
  const uploadConfig: TrackerConfig<UploadFile> = {
    type: 'upload',
    title: getUploadTitle(),
    titleColor: getUploadTitleColor(),
    actionButton:
      getUploadProgressCount() > 0 && uploadStore.handleAbortAll
        ? {
            text: 'Cancel',
            action: () => uploadStore.handleAbortAll(queryClient),
          }
        : undefined,

    onFolderClick: handleFolderClick,

    getLeftIcon: file => (
      <FileTypeIcon
        filename={file.name}
        mediaType={file.mediaType}
        mimeType={file.mimeType}
        size={20}
      />
    ),

    getRightIcon: file => {
      // Check if file is being cancelled (waiting for serverFileId)
      if (file.isCancelling) {
        return (
          <Tooltip className="text-black dark:text-white" content="Cancelling...">
            <div>
              <SpinnerIcon />
            </div>
          </Tooltip>
        )
      }

      if (file.status === FileStatus.FAILED || (file.status as string) === 'FAILED')
        return (
          <Tooltip
            content={file.errorMessage || 'Failed'}
            className={
              file.errorMessage?.includes('cancel') || file.errorMessage?.includes('abort')
                ? 'text-black dark:text-white'
                : 'text-red-600 dark:text-[#DD524C]'
            }
          >
            <div>
              {file.errorMessage?.includes('cancel') || file.errorMessage?.includes('abort') ? (
                <AbortIcon />
              ) : (
                <ErrorIcon />
              )}
            </div>
          </Tooltip>
        )
      if (file.status === FileStatus.AVAILABLE || (file.status as string) === 'AVAILABLE') {
        // Show warning icon for folders with failures
        const isFolder = file.mediaType === MediaType.FOLDER || file.uploadStrategy === 'folder'
        const hasFailures = (file.failedFileDetails?.length || 0) > 0

        if (isFolder && hasFailures) {
          // Use the same count as the modal (failedFileDetails.length) for consistency
          const actualFailedCount = file.failedFileDetails?.length || 0

          return (
            <Tooltip
              className="text-orange-600 dark:text-orange-400"
              content={`${actualFailedCount} file${actualFailedCount !== 1 ? 's' : ''} failed - Click to view details`}
            >
              <div
                aria-label={`View details for ${actualFailedCount} failed files`}
                role="button"
                tabIndex={0}
                onClick={e => {
                  e.stopPropagation()
                  handleFolderClick(file)
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    handleFolderClick(file)
                  }
                }}
              >
                <ExclamationTriangleIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </Tooltip>
          )
        }

        return <CheckIcon />
      }
      // Show spinner while cancelling
      if (file.isCancelling) {
        return <SpinnerIcon />
      }
      if (file.status === FileStatus.ENCRYPTING || (file.status as string) === 'ENCRYPTING') {
        return <SpinnerIcon />
      }
      if (file.status === FileStatus.UPLOADING || (file.status as string) === 'UPLOADING')
        return (
          <CancelableUploadIcon onCancel={() => uploadStore.handleAbort(file.id, queryClient)} />
        )
      if (file.status === FileStatus.FAILED || (file.status as string) === 'FAILED') {
        // Show error icon for failed uploads
        return <ErrorIcon />
      }

      return (
        <FileTypeIcon
          filename={file.name}
          mediaType={file.mediaType}
          mimeType={file.mimeType}
          size={20}
        />
      )
    },

    renderProgressContent: file => <UploadProgressBar file={file} />,
    renderStatusContent: () => null, // Status is now handled in UploadProgressBar

    getFileName: (file, hoveredId) => {
      // Show "Cancelling..." for files being cancelled
      if (file.isCancelling) {
        return hoveredId === file.id
          ? file.name.length > 35
            ? truncateTextMiddle(file.name, 35)
            : file.name
          : 'Cancelling...'
      }

      const isFailed = file.status === FileStatus.FAILED || (file.status as string) === 'FAILED'

      if (isFailed) {
        // Always show the file/folder name, never show error message as the name
        return file.name.length > 35 ? truncateTextMiddle(file.name, 35) : file.name
      }

      // Check if this is a folder with naming conflict
      const isFolder = file.mediaType === 'FOLDER' || file.uploadStrategy === 'folder'

      if (isFolder && file.originalName && file.serverName) {
        // For folders, check if there's a real naming conflict (server added suffix like (1), (2), etc.)
        const hasNamingConflict =
          file.originalName !== file.serverName &&
          ((file.serverName.includes('(') && file.serverName.includes(')')) ||
            file.serverName.trim() !== file.originalName.trim())

        if (hasNamingConflict) {
          const displayText = `${file.originalName} uploaded as ${file.serverName}`

          return displayText.length > 50 ? truncateTextMiddle(displayText, 50) : displayText
        }
      } else if (file.originalName && file.originalName !== file.name) {
        // For regular files, use the existing logic
        const displayText = `${file.originalName} uploaded as ${file.name}`

        return displayText.length > 50 ? truncateTextMiddle(displayText, 50) : displayText
      }

      // For folders, show the folder name (with retrying tag if applicable)
      if (isFolder) {
        const displayName = file.name.length > 35 ? truncateTextMiddle(file.name, 35) : file.name

        // [RETRY_FAILED] Show retrying indicator
        if ((file as any).isRetrying) {
          return `${displayName} (Retrying...)`
        }

        return displayName
      }

      // Enhanced display for folder uploads with real-time progress from WebSocket events
      if (
        isFolder &&
        (file as any).uploadedFiles !== undefined &&
        (file as any).totalFiles !== undefined
      ) {
        const folderFile = file as any
        // Use real-time progress data from WebSocket events
        const uploadProgress = `${folderFile.uploadedFiles}/${folderFile.totalFiles}`
        const encryptionProgress =
          folderFile.encryptedFiles !== undefined
            ? ` (${folderFile.encryptedFiles}/${folderFile.totalFiles} encrypted)`
            : ''
        const failedText =
          (folderFile.failedUploads || 0) + (folderFile.failedEncryptions || 0) > 0
            ? ` (${(folderFile.failedUploads || 0) + (folderFile.failedEncryptions || 0)} failed)`
            : ''

        const progressText = `${uploadProgress}${encryptionProgress}${failedText}`
        const displayText = `${file.name} ${progressText}`

        return displayText.length > 50 ? truncateTextMiddle(displayText, 50) : displayText
      }

      return file.name.length > 35 ? truncateTextMiddle(file.name, 35) : file.name
    },

    getFileTitle: file => {
      // Check if this is a folder with naming conflict
      const isFolder = file.mediaType === 'FOLDER' || file.uploadStrategy === 'folder'

      if (isFolder && file.originalName && file.serverName) {
        // For folders, check if there's a real naming conflict
        const hasNamingConflict =
          file.originalName !== file.serverName &&
          ((file.serverName.includes('(') && file.serverName.includes(')')) ||
            file.serverName.trim() !== file.originalName.trim())

        if (hasNamingConflict) {
          return `${file.originalName} uploaded as ${file.serverName}`
        }
      } else if (file.originalName && file.originalName !== file.name) {
        // For regular files, use the existing logic
        return `${file.originalName} uploaded as ${file.name}`
      }

      // Enhanced tooltip for folder uploads with combined progress format
      if (
        isFolder &&
        (file as any).uploadedFiles !== undefined &&
        (file as any).totalFiles !== undefined
      ) {
        const folderFile = file as any
        let statusText = ''

        // Add upload progress
        const uploadFailedText =
          folderFile.failedUploads && folderFile.failedUploads > 0
            ? ` (${folderFile.failedUploads} failed)`
            : ''

        statusText += `Upload ${folderFile.uploadedFiles}/${folderFile.totalFiles}${uploadFailedText}`

        // Add encryption progress
        if (folderFile.encryptedFiles !== undefined) {
          const encryptFailedText =
            folderFile.failedEncryptions && folderFile.failedEncryptions > 0
              ? ` (${folderFile.failedEncryptions} failed)`
              : ''

          statusText += ` | Encrypt ${encryptFailedText}`
        }

        return `${file.name}\n${statusText}`
      }

      return file.name
    },
  }

  // Download configuration
  const downloadConfig: TrackerConfig<ExtendedDownloadItemState> = {
    type: 'download',
    title: getDownloadSummary(downloadStore.items),
    titleColor: getDownloadTitleColor(),

    getLeftIcon: file => (
      <FileTypeIcon
        filename={file.name}
        mediaType={file.mediaType}
        mimeType={file.mimeType}
        size={20}
      />
    ),

    getRightIcon: item => {
      if (item.status === 'FAILED')
        return (
          <Tooltip
            content={item.errorMessage || 'Failed'}
            className={
              item.errorMessage?.includes('abort')
                ? 'text-black dark:text-white'
                : 'text-red-600 dark:text-[#DD524C]'
            }
          >
            <div>{item.errorMessage?.includes('abort') ? <AbortIcon /> : <ErrorIcon />}</div>
          </Tooltip>
        )
      if (item.status === 'AVAILABLE') return <CheckIcon />

      return <SpinnerIcon />
    },

    renderStatusContent: item => <StatusIndicator status={item.status} type="download" />,

    getFileName: item => item.name,

    getFileTitle: item => item.name,
  }

  // Convert download items to extended interface
  const extendedDownloadItems: ExtendedDownloadItemState[] = downloadStore.items.map(item => ({
    id: item.id,
    name: item.name,
    status: item.status,
    mediaType: item.mediaType,
    errorMessage: item.errorMessage,
    fileId: item.fileId,
    url: item.url,
    mimeType: item.mimeType,
  }))

  // Convert upload files - for folder uploads, only show the folder progress entry
  const uploadFiles: UploadFile[] = uploadStore.uploadingFiles
    .filter(f => {
      // Hide files marked as hidden (individual files in folder uploads)
      if ((f as any).isHidden) {
        return false
      }

      // For folder uploads, only show the folder progress entry (not individual files)
      const isFolderUpload = (f as any).isFolderUpload

      // If this is a folder upload, only show it if it's the main folder progress entry
      if (isFolderUpload) {
        return (f as any).uploadStrategy === 'folder'
      }

      // For regular files, show all files
      return true
    })
    .map(f => ({
      ...f,
      mediaType: f.mediaType,
      fileStatus: f.status,
      mimeType: f.mimeType,
      serverName: (f as any).serverName, // Include serverName for folder naming conflicts
      // For folder uploads, ensure we use the real-time progress data from WebSocket events
      uploadedFiles: (f as any).uploadedFiles,
      totalFiles: (f as any).totalFiles,
      failedUploads: (f as any).failedUploads,
      encryptedFiles: (f as any).encryptedFiles,
      failedEncryptions: (f as any).failedEncryptions,
      uploadedBytes: (f as any).uploadedBytes,
      totalBytes: (f as any).totalBytes,
      timeEstimate: (f as any).timeEstimate,
    }))

  // Calculate active counts for badges
  const activeUploadsCount = uploadStore.uploadingFiles.filter(
    f =>
      f.status === FileStatus.UPLOADING ||
      f.status === FileStatus.ENCRYPTING ||
      (f.status as string) === 'UPLOADING' ||
      (f.status as string) === 'ENCRYPTING',
  ).length

  const activeDownloadsCount = downloadStore.items.filter(
    i => i.status === 'PROCESSING' || i.status === 'DECRYPTING' || i.status === 'PREPARING',
  ).length

  return (
    <>
      {/* Mobile Header with Popover */}
      <div className="flex justify-between items-center lg:hidden w-full">
        {pathname.includes('/profile') ? null : (
          <div>
            <LogoIcon className="text-black dark:text-white" />
          </div>
        )}
        {(uploadVisible || downloadVisible) && isTablet && (
          <div className="lg:hidden  flex gap-2">
            {/* Upload Button */}
            {uploadVisible && (
              <Popover
                isOpen={isPopoverOpen && activeTab === 'upload'}
                offset={24}
                placement="bottom"
                onOpenChange={open => {
                  if (open) {
                    setIsPopoverOpen(!isPopoverOpen)
                    setActiveTab('upload')
                  } else {
                    setIsPopoverOpen(false)
                  }
                }}
              >
                <PopoverTrigger>
                  <Badge
                    color={activeUploadsCount > 0 ? 'primary' : 'default'}
                    isInvisible={uploadStore.uploadingFiles.length === 0}
                    shape="circle"
                    content={
                      activeUploadsCount > 0
                        ? activeUploadsCount
                        : uploadStore.uploadingFiles.length
                    }
                    onClick={() => {
                      setIsPopoverOpen(!isPopoverOpen)
                      setActiveTab('upload')
                    }}
                  >
                    <Button
                      isIconOnly
                      aria-label="upload progress"
                      radius="full"
                      variant="light"
                      onPress={() => {
                        setIsPopoverOpen(!isPopoverOpen)
                        setActiveTab('upload')
                      }}
                    >
                      <ArrowUpTrayIcon className="flex-shrink-0 w-6" />
                    </Button>
                  </Badge>
                </PopoverTrigger>
                <PopoverContent className="bg-white dark:!bg-[#323232]">
                  <div>
                    <SharedProgressTracker
                      config={uploadConfig}
                      isExpanded={isUploadExpanded}
                      isMobile={true}
                      isOpen={uploadStore.isUploadModalOpen}
                      items={uploadFiles}
                      onClose={handleUploadClose}
                      onRetry={handleUploadRetry}
                      onToggleExpanded={() => setIsUploadExpanded(!isUploadExpanded)}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {/* Download Button */}
            {downloadVisible && (
              <Popover
                isOpen={isPopoverOpen && activeTab === 'download'}
                offset={24}
                placement="bottom"
                onOpenChange={open => {
                  if (open) {
                    setIsPopoverOpen(!isPopoverOpen)
                    setActiveTab('download')
                  } else {
                    setIsPopoverOpen(false)
                  }
                }}
              >
                <PopoverTrigger>
                  <Badge
                    color={activeDownloadsCount > 0 ? 'primary' : 'default'}
                    isInvisible={downloadStore.items.length === 0}
                    shape="circle"
                    content={
                      activeDownloadsCount > 0 ? activeDownloadsCount : downloadStore.items.length
                    }
                    onClick={() => {
                      setIsPopoverOpen(!isPopoverOpen)
                      setActiveTab('download')
                    }}
                  >
                    <Button
                      isIconOnly
                      aria-label="download progress"
                      radius="full"
                      variant="light"
                      onPress={() => {
                        setIsPopoverOpen(!isPopoverOpen)
                        setActiveTab('download')
                      }}
                    >
                      <ArrowDownTrayIcon className="flex-shrink-0 w-6" />
                    </Button>
                  </Badge>
                </PopoverTrigger>
                <PopoverContent className="bg-white dark:!bg-[#323232]">
                  <div>
                    <SharedProgressTracker
                      config={downloadConfig}
                      isExpanded={!downloadStore.isMinimized}
                      isMobile={true}
                      isOpen={downloadStore.isOpen}
                      items={extendedDownloadItems}
                      onClose={handleDownloadClose}
                      onRetry={handleDownloadRetry}
                      onToggleExpanded={downloadStore.toggle}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}
      </div>

      {/* Desktop Progress Trackers */}
      {uploadVisible && (
        <div className="fixed bottom-0 z-50 hidden lg:block" style={positions.upload}>
          <SharedProgressTracker
            config={uploadConfig}
            isExpanded={isUploadExpanded}
            isOpen={uploadStore.isUploadModalOpen}
            items={uploadFiles}
            onClose={handleUploadClose}
            onRetry={handleUploadRetry}
            onToggleExpanded={() => setIsUploadExpanded(!isUploadExpanded)}
          />
        </div>
      )}

      {downloadVisible && (
        <div className="fixed bottom-0 z-50 hidden lg:block" style={positions.download}>
          <SharedProgressTracker
            config={downloadConfig}
            isExpanded={!downloadStore.isMinimized}
            isOpen={downloadStore.isOpen}
            items={extendedDownloadItems}
            onClose={handleDownloadClose}
            onRetry={handleDownloadRetry}
            onToggleExpanded={downloadStore.toggle}
          />
        </div>
      )}
    </>
  )
}

export default ProgressTracker
